import { AlertBanner } from '../design-system/components/AlertBanner';
import { useFirePanelButtonLabel } from '../api/hooks/useFireMode';
import { useBackendStatus } from '../state/useBackendStatus';
import { useFireAlarm } from '../state/useFireAlarm';
import { formatDateTime } from '../utils/date-format';
import './GlobalStatusBanner.css';

export function GlobalStatusBanner() {
  const { status } = useBackendStatus();
  const { isFireAlarmActive, fireAlarmTriggeredAt, openFireModePanel } = useFireAlarm();
  // "Fire mode status" for a reader who can only look (review BC-F12).
  const firePanelLabel = useFirePanelButtonLabel();

  if (isFireAlarmActive) {
    // Evacuation copy, so it says what is true of the BUILDING and what
    // this operator can do about it. It used to send the reader to "the
    // FireAlarmOverlay" — a React component name, printed to a duty
    // manager at 2am — for a timestamp this banner can simply state.
    return (
      // "Fire alarm" for a drill too, on purpose (`attention/words.ts`):
      // the live key carries no drill flag, so a title taken from the
      // 30 s REST read could call a real alarm a drill.
      <AlertBanner
        variant="error"
        title="Fire alarm active"
        actions={
          <button
            type="button"
            className="nx-fire-banner__btn"
            onClick={openFireModePanel}
            data-testid="banner-open-fire-panel"
          >
            {firePanelLabel}
          </button>
        }
      >
        Follow the building&apos;s evacuation procedure.
        {fireAlarmTriggeredAt
          ? ` Raised ${formatDateTime(fireAlarmTriggeredAt)}.`
          : ''}{' '}
        Robot dispatch is held for this site until a supervisor resolves the event.
      </AlertBanner>
    );
  }
  if (status.readiness === 'unreachable') {
    return (
      <AlertBanner variant="error" title="API unreachable">
        Live data is unavailable. The console is showing the last cached state if any.
      </AlertBanner>
    );
  }
  // Hotel words (P9, 2026-10-31); the title stays because the manual
  // quotes it. Two bodies, because the two causes do different things:
  // a degraded readiness read pauses new tasks and calendar changes
  // (`useTaskDispatchGate`), and a dropped live connection pauses
  // nothing, it only leaves the screen behind. One body used to say
  // "Write actions are paused" for both.
  if (status.readiness === 'degraded') {
    return (
      <AlertBanner variant="warning" title="Live data degraded">
        Part of the system is not ready. What you see may be out of date, and new tasks and
        calendar changes are paused until it is back.
      </AlertBanner>
    );
  }
  if (status.ws === 'closed' || status.ws === 'stale') {
    return (
      <AlertBanner variant="warning" title="Live data degraded">
        The live connection has dropped. What you see may be out of date until it reconnects.
      </AlertBanner>
    );
  }
  return null;
}
